/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

/* -------------------------------------------------------------------------- */
/*                               CONFIG PRESETS                               */
/* -------------------------------------------------------------------------- */

const BOOK_SIZES = {
  default: {
    id: "default",
    label: "Padrão [15,5 x 23 cm]",
    height: 230,
    width: 155,
  },
  pocket: {
    id: "pocket",
    label: "Livro de Bolso [11 x 18 cm]",
    height: 180,
    width: 110,
  },
  medium: {
    id: "medium",
    label: "Médio [14 x 21 cm]",
    height: 210,
    width: 140,
  },
  kids: {
    id: "kids",
    label: "Infantil [20 x 20 cm]",
    height: 200,
    width: 200,
  },
  a4: {
    id: "a4",
    label: "Técnico [A4]",
    height: 297,
    width: 210,
  },
  custom: {
    id: "custom",
    label: "Personalizado",
  },
};

const normalizeSliderValue = (value) => (Array.isArray(value) ? value[0] : value);

/* -------------------------------------------------------------------------- */
/*                          HELPERS / PRICE SIMULATION                        */
/* -------------------------------------------------------------------------- */

const PAPER_TYPES = [
  {
    id: "white",
    title: "Papel Branco",
    subtitle: "Offset Branco 75g/m² (igual ao sulfite)",
    badge: "P&B",
    // coeficientes genéricos
    baseCost: 6.0,
    costPerPage: 0.18,
    areaFactor: 0.000015,
  },
  {
    id: "cream",
    title: "Papel Creme",
    subtitle: "Avena ou Pólen, 80g/m² (papel creme)",
    badge: "P&B",
    baseCost: 6.5,
    costPerPage: 0.19,
    areaFactor: 0.000016,
  },
  {
    id: "couche",
    title: "Papel Couchê",
    subtitle: "Couchê fosco branco, 90g/m² (apenas impressão colorida)",
    badge: "Color",
    baseCost: 10.0,
    costPerPage: 0.3,
    areaFactor: 0.00002,
  },
];

// formatar moeda BR
const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

// parse simples de "10,00" -> 10
const parseCurrencyPtBr = (value) => {
  if (!value) return 0;
  const normalized = value.replace(/[R$\s.]/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Calcula valores financeiros para um tipo de papel.
 * Esta fórmula é APENAS UMA APROXIMAÇÃO.
 * Depois que você tiver os coeficientes exatos, é só ajustar aqui.
 */
const calculatePaperValues = ({ height, width, pages, hasFlap, flapSize, authorGain, paper }) => {
  const area = height * width; // mm²
  const baseArea = 155 * 230; // área de referência (formato padrão)
  const areaMultiplier = area / baseArea;

  // custo base proporcional ao tamanho
  let finalCost =
    paper.baseCost * areaMultiplier +
    paper.costPerPage * pages * areaMultiplier +
    paper.areaFactor * area;

  // ajuste de custo se tiver orelha (bem genérico, só para ter impacto)
  if (hasFlap) {
    const flapArea = flapSize * height * 2; // duas orelhas
    finalCost += paper.areaFactor * flapArea * 0.8;
  }

  // Ganha do autor (valor que ele escolhe)
  const authorValue = authorGain;

  // Loja Bublique sua Obra = custo + ganho do autor
  const storePrice = finalCost + authorValue;

  // Valor de capa: usamos a mesma lógica que parece acontecer nos prints:
  // loja ~ 79,166% do valor de capa -> capa = loja * (24 / 19)
  const coverPrice = storePrice * (24 / 19);

  return {
    coverPrice,
    storePrice,
    finalCost,
    authorValue,
  };
};

/* -------------------------------------------------------------------------- */
/*                             PRICE SIMULATOR UI                             */
/* -------------------------------------------------------------------------- */

function PriceSimulator({ height, width, pages, hasFlap, flapSize }) {
  const [authorGainInput, setAuthorGainInput] = useState("10,00");

  const authorGain = parseCurrencyPtBr(authorGainInput);

  const handleAuthorGainChange = (event) => {
    setAuthorGainInput(event.target.value);
  };

  const rows = PAPER_TYPES.map((paper) => {
    const values = calculatePaperValues({
      height,
      width,
      pages,
      hasFlap,
      flapSize,
      authorGain,
      paper,
    });

    return {
      ...paper,
      ...values,
    };
  });

  return (
    <MDBox mt={6}>
      {/* Título e input de quanto o autor quer ganhar */}
      <MDBox mb={3}>
        <MDTypography variant="h6" fontWeight="bold" color="text">
          $&nbsp;Simule o valor de venda do seu livro
        </MDTypography>

        <MDBox mt={2}>
          <MDTypography variant="button" color="text">
            Escolha quanto você quer ganhar pela venda de cada Livro:
          </MDTypography>

          <Box mt={2} maxWidth={260}>
            <TextField
              fullWidth
              value={authorGainInput}
              onChange={handleAuthorGainChange}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              helperText="Mínimo de R$ 0,01."
            />
          </Box>
        </MDBox>
      </MDBox>

      {/* Tabela de preços */}
      <Box
        sx={{
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          overflowX: "auto",
        }}
      >
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            "& th, & td": {
              borderBottom: "1px solid #eee",
              padding: "12px 16px",
              textAlign: "center",
              fontSize: "0.9rem",
            },
            "& th:first-of-type, & td:first-of-type": {
              textAlign: "left",
            },
          }}
        >
          <thead style={{ backgroundColor: "#b0cef5" }}>
            <tr>
              <th style={{ minWidth: 210 }}>Valores de venda um a um</th>
              {rows.map((paper) => (
                <th key={paper.id}>
                  <Box
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                      position: "relative",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="button" sx={{ fontWeight: "bold", color: "#087f23" }}>
                      {paper.title}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2 }}>
                      {paper.subtitle}
                    </Typography>
                    <Box
                      sx={{
                        position: "absolute",
                        top: -18,
                        right: "50%",
                        transform: "translateX(50%)",
                        backgroundColor: "#333",
                        color: "#fff",
                        borderRadius: "999px",
                        px: 1.5,
                        py: 0.2,
                        fontSize: "0.7rem",
                      }}
                    >
                      {paper.badge}
                    </Box>
                  </Box>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* Valor de capa */}
            <tr>
              <td>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  VALOR DE CAPA:
                </Typography>
                <Typography variant="caption" display="block">
                  Valor de venda na Amazon já com a taxa do marketplace
                </Typography>
              </td>
              {rows.map((paper) => (
                <td key={`${paper.id}-coverPrice`}>{formatCurrency(paper.coverPrice)}</td>
              ))}
            </tr>

            {/* Valor de venda na loja */}
            <tr>
              <td>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  VALOR DE VENDA NA LOJA Bublique sua Obra
                </Typography>
                <Typography variant="caption" display="block">
                  Com desconto sobre o valor de capa
                </Typography>
              </td>
              {rows.map((paper) => (
                <td key={`${paper.id}-storePrice`}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(paper.storePrice)}
                  </Typography>
                </td>
              ))}
            </tr>

            {/* Custo final do livro */}
            <tr>
              <td>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  • CUSTO FINAL DO LIVRO
                </Typography>
                <Typography variant="caption" display="block">
                  Produção do Livro + Taxas e Impostos
                </Typography>
              </td>
              {rows.map((paper) => (
                <td key={`${paper.id}-finalCost`}>{formatCurrency(paper.finalCost)}</td>
              ))}
            </tr>

            {/* Ganho do autor */}
            <tr>
              <td>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  • GANHO DO AUTOR
                </Typography>
                <Typography variant="caption" display="block">
                  Valor a receber pela venda de cada exemplar
                  <br />
                  Você pode escolher o quanto quer ganhar!
                </Typography>
              </td>
              {rows.map((paper) => (
                <td key={`${paper.id}-author`}>{formatCurrency(paper.authorValue)}</td>
              ))}
            </tr>
          </tbody>
        </Box>
      </Box>
    </MDBox>
  );
}

/* -------------------------------------------------------------------------- */
/*                         COMPONENTE BOOK COST CALC                          */
/* -------------------------------------------------------------------------- */

function BookCostCalculator() {
  const [sizeId, setSizeId] = useState("pocket");
  const [height, setHeight] = useState(180); // mm
  const [width, setWidth] = useState(110); // mm
  const [pages, setPages] = useState(200);
  const [hasFlap, setHasFlap] = useState(false);
  const [flapSize, setFlapSize] = useState(67); // mm da orelha

  const isCustom = sizeId === "custom";

  const handleChangeSize = (event) => {
    const newSizeId = event.target.value;
    setSizeId(newSizeId);

    const preset = BOOK_SIZES[newSizeId];

    // Quando não for personalizado, travamos nas medidas do preset
    if (preset && preset.height && preset.width) {
      setHeight(preset.height);
      setWidth(preset.width);
    }
  };

  /* ------------------------- PREVIEW COM PROPORÇÃO OK ------------------------- */

  const MAX_PREVIEW_HEIGHT = 320; // limite visual de altura
  const MAX_PREVIEW_WIDTH = 200; // limite visual de largura

  const scaleH = MAX_PREVIEW_HEIGHT / height;
  const scaleW = MAX_PREVIEW_WIDTH / width;
  const scale = Math.min(scaleH, scaleW);

  const previewHeight = height * scale;
  const previewWidth = width * scale;

  return (
    <MDBox px={3} pb={4}>
      <Grid container spacing={4}>
        {/* Coluna esquerda: controles */}
        <Grid item xs={12} md={7}>
          {/* Escolha o tamanho */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              Escolha o tamanho
            </MDTypography>

            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="book-size-label">Tamanho</InputLabel>
              <Select
                style={{ height: 40 }}
                labelId="book-size-label"
                value={sizeId}
                label="Tamanho"
                onChange={handleChangeSize}
              >
                {Object.values(BOOK_SIZES).map((size) => (
                  <MenuItem key={size.id} value={size.id}>
                    {size.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>

          {/* Altura */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              Altura (mm)
            </MDTypography>
            <Grid container alignItems="center" spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={8}>
                <Slider
                  value={height}
                  min={110}
                  max={297}
                  step={1}
                  disabled={!isCustom}
                  onChange={(_, value) =>
                    setHeight(Math.min(297, Math.max(110, normalizeSliderValue(value))))
                  }
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">MIN: 110</Typography>
                  <Typography variant="caption">MAX: 297</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={height}
                  onChange={(e) =>
                    setHeight(Math.min(297, Math.max(110, Number(e.target.value) || 110)))
                  }
                />
              </Grid>
            </Grid>
          </MDBox>

          {/* Largura */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              Largura (mm)
            </MDTypography>
            <Grid container alignItems="center" spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={8}>
                <Slider
                  value={width}
                  min={70}
                  max={210}
                  step={1}
                  disabled={!isCustom}
                  onChange={(_, value) =>
                    setWidth(Math.min(210, Math.max(70, normalizeSliderValue(value))))
                  }
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">MIN: 70</Typography>
                  <Typography variant="caption">MAX: 210</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={width}
                  onChange={(e) =>
                    setWidth(Math.min(210, Math.max(70, Number(e.target.value) || 70)))
                  }
                />
              </Grid>
            </Grid>
          </MDBox>

          {/* Número de páginas */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              Nº de Páginas
            </MDTypography>
            <Grid container alignItems="center" spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={8}>
                <Slider
                  value={pages}
                  min={4}
                  max={1000}
                  step={1}
                  onChange={(_, value) =>
                    setPages(Math.min(1000, Math.max(4, normalizeSliderValue(value) || 4)))
                  }
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">MIN: 4</Typography>
                  <Typography variant="caption">MAX: 1000</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={pages}
                  onChange={(e) =>
                    setPages(Math.min(1000, Math.max(4, Number(e.target.value) || 4)))
                  }
                />
              </Grid>
            </Grid>
          </MDBox>

          {/* Livro com orelha */}
          <MDBox mt={2}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <MDTypography variant="button" fontWeight="bold" color="text">
                  Livro COM orelha?
                </MDTypography>
              </Grid>
              <Grid item>
                <Switch checked={hasFlap} onChange={(e) => setHasFlap(e.target.checked)} />
              </Grid>
              <Grid item>
                <MDTypography variant="button" color="text">
                  {hasFlap ? "sim" : "não"}
                </MDTypography>
              </Grid>
            </Grid>

            {hasFlap && (
              <MDBox mt={2}>
                <MDTypography variant="caption" color="text">
                  As orelhas do seu livro podem ter de 50mm a 100mm. Informe abaixo o tamanho
                  desejado.
                </MDTypography>

                <Grid container alignItems="center" spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={8}>
                    <Slider
                      value={flapSize}
                      min={50}
                      max={100}
                      step={1}
                      onChange={(_, value) =>
                        setFlapSize(Math.min(100, Math.max(50, normalizeSliderValue(value))))
                      }
                    />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">MIN: 50</Typography>
                      <Typography variant="caption">MAX: 100</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={flapSize}
                      onChange={(e) =>
                        setFlapSize(Math.min(100, Math.max(50, Number(e.target.value) || 50)))
                      }
                    />
                  </Grid>
                </Grid>
              </MDBox>
            )}
          </MDBox>
        </Grid>

        {/* Coluna direita: preview do livro */}
        <Grid item xs={12} md={5}>
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Box
              sx={{
                width: previewWidth,
                height: previewHeight,
                backgroundColor: "#087f23",
                borderRadius: "6px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "width .2s, height .2s",
              }}
            >
              {/* Altura */}
              <Box
                sx={{
                  position: "absolute",
                  left: -40,
                  top: "50%",
                  transform: "translateY(-50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="#087f23">
                  ↑
                </Typography>
                <Typography variant="body2" color="#087f23">
                  {height}
                </Typography>
                <Typography variant="caption" color="#087f23">
                  ↓
                </Typography>
              </Box>

              {/* Largura */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  left: "50%",
                  transform: "translateX(-50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="#087f23">
                  ← {width} →
                </Typography>
              </Box>

              {/* Texto central */}
              <Typography variant="body2" color="#FFFFFF" align="center">
                {pages} páginas
                {hasFlap ? ` • orelha ${flapSize}mm` : ""}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Simulador de preço usando as mesmas informações do livro */}
      <PriceSimulator
        height={height}
        width={width}
        pages={pages}
        hasFlap={hasFlap}
        flapSize={flapSize}
      />
    </MDBox>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   TABLES                                   */
/* -------------------------------------------------------------------------- */

function Tables() {
  return (
    <DashboardLayout>
      {/* <DashboardNavbar /> */}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Insira as informações para calcularmos o preço de custo do seu livro
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <BookCostCalculator />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {/* <Footer /> */}
    </DashboardLayout>
  );
}

export default Tables;
